import { DefaultTrackingService, Device, GtmTrackingHandler, TrackingProvider } from '@skoda/k2tools-app-services/lib';
import { AppConfig } from '../appConfig';
import { SettingsService } from '../services/settingsService';
import { removeNullAttributes } from '../utils/helpers';
import { AppWebName, ICommonTrackingData, ITrackingObject, TrackingActions, TrackingMatrix } from './trackingModels';

export class TrackingPrepare extends TrackingProvider<ITrackingObject> {
	private _device: Device;
	private _appConfig: AppConfig;
	private _settingsService: SettingsService;
	private _disableTracking: boolean;
	private _trackingOverride: Partial<ITrackingObject>;

	constructor(device: Device, appConfig: AppConfig, settingsService: SettingsService, disableTracking: boolean, trackingOverride: Partial<ITrackingObject>) {
		super(new DefaultTrackingService(), new GtmTrackingHandler());
		this._device = device;
		this._appConfig = appConfig;
		this._disableTracking = disableTracking;
		this._trackingOverride = trackingOverride;
		this._settingsService = settingsService;
	}

	public trackAction = (trackingObject: ITrackingObject) => {
		if (this._disableTracking) return;
		super.trackAction(removeNullAttributes<ITrackingObject>({ ...trackingObject, ...this._trackingOverride }));
	};

	public trackOrientationChange = () => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.orientationChange, 'orientation_change', 'trackEvent'),
			eventCategory: 'All pages',
			eventAction: TrackingActions.ActionOrientationChange,
			eventLabel: this._device.orientationFullName(),
			eventNonInteraction: 'true',
			'page.ResponsiveLayout': this._device.fullName(),
			'page.Orientation': this._device.orientationFullName(),
		});
	};

	public trackScroll = (percentageString: string) => {
		if (window.OwnersManuals.tracking?.enabled === false) return;
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.scrollDepth, 'scroll_down', 'trackEvent'),
			eventCategory: 'All pages',
			eventAction: TrackingActions.ScrollDown,
			eventLabel: `Scroll depth: ${percentageString}`,
			'page.ScrollDepth': percentageString,
		});
	};

	public trackHomepageView = () => {
		this.trackAction({
			...this.getPageViewCommonTrackingData(TrackingMatrix.pageView, 'page_view'),
			documentPath: '/Models',
		});
	};

	public trackDetailView = (carModel: string, omsId: string) => {
		this.trackAction({
			...this.getPageViewCommonTrackingData(TrackingMatrix.pageView, 'page_view'),
			documentPath: '/Detail',
			'car.Model': carModel,
			'car.OmsId': omsId,
		});
	};

	public trackErrorPageView = (carModel: string) => {
		this.trackAction({
			...this.getPageViewCommonTrackingData(TrackingMatrix.errorPageView, 'page_view'),
			documentPath: '/page-not-found',
			'car.Model': carModel,
		});
	};

	public trackSearchError = (errorType: string, carModel: string, omsId: string) => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.searchError, 'manual_errortype', 'trackEvent'),
			eventCategory: "Owner's manual",
			eventAction: `Search error`,
			eventLabel: errorType,
			'car.Model': carModel,
			'car.OmsId': omsId,
		});
	};

	public trackTabChange = (isCurrentModel: boolean) => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.tabChange, 'tab_change', 'trackEvent'),
			eventCategory: 'Tab change',
			eventLabel: `Current/Previous models`,
			eventAction: `Choose model - ${isCurrentModel ? 'Current models' : 'Previous models'}`,
		});
	};

	public trackFocus = () => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.focus, 'manual_focus', 'trackEvent'),
			eventCategory: "Owner's manual",
			eventAction: `VIN`,
			eventLabel: 'Focus',
		});
	};

	public trackFamilyModelClick = (isCurrentModel: boolean, carModel: string) => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.buttonClick, 'button_click', 'trackEvent'),
			eventCategory: 'Button click',
			eventAction: `Owner's manual - Choose model - ${isCurrentModel ? 'Current models' : 'Previous models'}`,
			eventLabel: `Model_${carModel}`,
		});
	};

	public trackDownloadOnlineManual = (carModel: string, omsId: string, isMainManual: boolean, manualId: string) => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.downloadOnlineManual, 'manual_open', 'trackEvent'),
			eventCategory: "Owner's manual",
			eventAction: 'Open manual - PDF',
			eventLabel: `${isMainManual ? 'Car manual' : 'Other manual'} - ${manualId}`,
			'car.Model': carModel,
			'car.OmsId': omsId,
		});
	};

	public trackOpenTPManual = (carModel: string, omsId: string, partNumber: string) => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.downloadOnlineManual, 'manual_open', 'trackEvent'),
			eventCategory: "Owner's manual",
			eventAction: 'Open manual - Topic Pilot',
			eventLabel: `Car manual - ${partNumber}`,
			'car.Model': carModel,
			'car.OmsId': omsId,
		});
	};

	public trackSearch = (isVinSearch: boolean, edition: string, market: string, language: string, carModel: string, omsId: string) => {
		this.trackAction({
			...this.getCommonTrackingData(
				TrackingMatrix.search,
				'manual_search',
				'trackEvent',
				window.location.href.includes('Models') ? 'Homepage' : 'Detail'
			),
			eventCategory: "Owner's manual",
			eventAction: isVinSearch ? 'VIN Search' : 'Model Search',
			eventLabel: !isVinSearch ? `${edition} - ${market ? market : ''} - ${language}` : `${language}`,
			'car.Model': carModel,
			'car.OmsId': omsId,
		});
	};

	public trackSearchResult = (resultManualsCount: string, interaction: boolean, carModel: string, omsId: string) => {
		this.trackAction({
			...this.getCommonTrackingData(TrackingMatrix.searchResults, 'manual_searchresults', 'trackEvent'),
			eventCategory: "Owner's manual",
			eventAction: 'Search Results',
			eventLabel: resultManualsCount,
			eventNonInteraction: interaction ? 'true' : 'false',
			'car.Model': carModel,
			'car.OmsId': omsId,
		});
	};

	private getPageViewCommonTrackingData = (matrixID: TrackingMatrix, appWebName: AppWebName): ICommonTrackingData => ({
		...this.getCommonTrackingData(matrixID, appWebName, 'pageView', document.title),
		'page.ResponsiveLayout': this._device.fullName(),
		'page.Orientation': this._device.orientationFullName(),
	});

	private getCommonTrackingData = (
		matrixID: TrackingMatrix,
		appWebName: AppWebName,
		event: ICommonTrackingData['event'],
		pageName: string = null
	): ICommonTrackingData => ({
		matrixId: matrixID,
		event: event,
		'appweb.Name': appWebName,
		'importer.BID': this._appConfig.bid,
		'page.Group': "Owner's manual NG",
		'page.Name': pageName,
		'page.LanguageVersion': this._appConfig.culture,
		'page.Context': 'Importer',
		'web.environment': this._settingsService.data.environment,
	});
}
